import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { teamRepository } from '../repositories/team.repository';
import { orgKeys } from '../services/organization.keys';
import { useOrganization } from './useOrganization';
import type { UserInvitation, AppRole } from '../types/organization.types';

export type { UserInvitation };

export function useUserInvitations() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: orgKeys.userInvitations(orgId),
    queryFn: async (): Promise<UserInvitation[]> => {
      return teamRepository.getInvitations();
    },
    enabled: true,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async ({ email, role }: { email: string; role: AppRole }) => {
      if (!orgId) {
        throw new Error('No organization found for current user');
      }
      return teamRepository.inviteUser(orgId, email, role);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.userInvitations(orgId) });
      queryClient.invalidateQueries({ queryKey: orgKeys.legacyUserInvitations() });
      toast({
        title: 'Invitation Sent',
        description: 'An invitation has been sent with role assignment.',
      });
    },
    onError: (error: any) => {
      console.error('Error in invite user process:', error);
      toast({
        title: 'Failed to Send Invitation',
        description: error?.message || 'An error occurred while sending the invitation.',
        variant: 'destructive',
      });
    },
  });
}

export function useCancelInvitation() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async (invitationId: string) => {
      await teamRepository.cancelInvitation(invitationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.userInvitations(orgId) });
      queryClient.invalidateQueries({ queryKey: orgKeys.legacyUserInvitations() });
      toast({
        title: 'Invitation Cancelled',
        description: 'The invitation has been cancelled successfully.',
      });
    },
    onError: (error: any) => {
      console.error('Error cancelling invitation:', error);
      toast({
        title: 'Failed to Cancel Invitation',
        description: error?.message || 'An error occurred while cancelling the invitation.',
        variant: 'destructive',
      });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useMutation({
    mutationFn: async (invitationId: string) => {
      return teamRepository.resendInvitation(invitationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.userInvitations(orgId) });
      queryClient.invalidateQueries({ queryKey: orgKeys.legacyUserInvitations() });
      queryClient.invalidateQueries({ queryKey: ['user-invitations'] });
      toast({
        title: 'Invitation resent successfully',
        description: 'The user will receive a new invitation email.',
      });
    },
    onError: (error: any) => {
      console.error('Error resending invitation:', error);
      toast({
        title: 'Error resending invitation',
        description: error?.message || 'Failed to resend invitation',
        variant: 'destructive',
      });
    },
  });
}

